import { useState, useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";

/**
 * usePresence — Real-time collaboration presence hook.
 * Connects to /api/ws/presence/{userId}, tracks page navigation,
 * typing indicators, and receives other users' presence updates.
 */
export function usePresence(userId) {
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [typingUsers, setTypingUsers] = useState({}); // { blockId: { user_id, name, avatar, block_label } }
  const wsRef = useRef(null);
  const location = useLocation();
  const reconnectRef = useRef(null);
  const locationRef = useRef(location.pathname);
  locationRef.current = location.pathname;

  const connect = useCallback(() => {
    if (!userId) return;
    const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
    const base = process.env.REACT_APP_BACKEND_URL?.replace(/^https?:/, proto);
    if (!base) return;

    const ws = new WebSocket(`${base}/api/ws/presence/${userId}`);
    wsRef.current = ws;

    ws.onopen = () => {
      ws.send(JSON.stringify({ type: "page_change", page: locationRef.current }));
    };

    ws.onmessage = (evt) => {
      try {
        const msg = JSON.parse(evt.data);
        if (msg.type === "presence_update") {
          setOnlineUsers(msg.users || []);
        } else if (msg.type === "ping") {
          ws.send("ping");
        } else if (msg.type === "typing_indicator") {
          const key = msg.user_id;
          setTypingUsers(prev => ({
            ...prev,
            [key]: { ...msg, expires: Date.now() + 4000 }
          }));
        } else if (msg.type === "typing_stopped") {
          setTypingUsers(prev => {
            const next = { ...prev };
            delete next[msg.user_id];
            return next;
          });
        }
      } catch {}
    };

    ws.onclose = () => {
      reconnectRef.current = setTimeout(connect, 5000);
    };
    ws.onerror = () => ws.close();
  }, [userId]);

  useEffect(() => {
    connect();
    return () => {
      clearTimeout(reconnectRef.current);
      wsRef.current?.close();
    };
  }, [connect]);

  // Notify page changes
  useEffect(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      const pathParts = location.pathname.split("/");
      const documentId = pathParts.length > 2 ? pathParts.slice(2).join("/") : null;
      wsRef.current.send(JSON.stringify({
        type: "page_change",
        page: location.pathname,
        document_id: documentId,
      }));
    }
  }, [location.pathname]);

  // Clean expired typing indicators
  useEffect(() => {
    const interval = setInterval(() => {
      setTypingUsers(prev => {
        const now = Date.now();
        const next = {};
        for (const [k, v] of Object.entries(prev)) {
          if (v.expires > now) next[k] = v;
        }
        return Object.keys(next).length !== Object.keys(prev).length ? next : prev;
      });
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  const sendCursor = useCallback((cursor) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: "cursor", cursor }));
    }
  }, []);

  const sendTyping = useCallback((blockId, blockLabel) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: "typing",
        block_id: blockId,
        block_label: blockLabel || "a section",
      }));
    }
  }, []);

  const sendTypingStopped = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: "typing_stopped" }));
    }
  }, []);

  return { onlineUsers, typingUsers, sendCursor, sendTyping, sendTypingStopped };
}
